import { ContractsRepository } from '../repositories/contracts.repository'
import {
    ContractsAttachDocumentResponseT,
    ContractsGetToBeAgreedParamsT,
    ContractsDownloadContractResponseT,
    ContractsDownloadDocumentResponseT,
    ContractsExportExcelParamsT,
    ContractsGetContractResponseT,
    ContractsGetListResponseT,
    ContractsGetParamsT,
    ContractsGetStatusesResponseT,
    ContractsGetToBeAgreedListResponseT,
    ContractsGetTypesResponseT,
    ContractsSendTranscriptParamsT,
    ContractsShowContractResponseT,
    ContractsShowDocumentResponseT,
    ContractsGetTypesParamsT,
    ContractsGetContractByBinResponseT,
    ContractsRegisterGetParams,
    ContractsRegisterGetResponse,
    ContractsRegisterExportExcelParams,
    GetContractRegisterResponse,
    CreateContractRegisterPayload,
    EditContractRegisterPayload,
    GetTemplatesParams,
    GetTemplatesResponse,
    DownloadLogistBidResponse,
    ContractsGetServicesResponse,
    GetConfigListParams,
    GetConfigListResponse,
    GetConfigValueByKeyResponse,
} from '../types/contracts.types'

const contractsRepository = new ContractsRepository()

export class ContractsService {
    async getList(params: ContractsGetParamsT): Promise<ContractsGetListResponseT> {
        const response = await contractsRepository.getList(params)
        return response.data
    }

    async getServices(): Promise<ContractsGetServicesResponse> {
        const response = await contractsRepository.getServices()
        return response.data
    }

    async getTemplates(params: GetTemplatesParams): Promise<GetTemplatesResponse> {
        const response = await contractsRepository.getTemplates(params)
        return response.data
    }

    async getArchiveList(params: ContractsGetParamsT): Promise<ContractsGetListResponseT> {
        const response = await contractsRepository.getArchiveList(params)
        return response.data
    }

    async getRegisterList(
        params: ContractsRegisterGetParams
    ): Promise<ContractsRegisterGetResponse> {
        const response = await contractsRepository.getRegisterList(params)
        return response.data
    }

    async getToBeAgreedList(
        params: ContractsGetToBeAgreedParamsT
    ): Promise<ContractsGetToBeAgreedListResponseT> {
        const response = await contractsRepository.getToBeAgreedList(params)
        return response.data
    }

    async getContract(contract_id: number): Promise<ContractsGetContractResponseT> {
        const response = await contractsRepository.getContract(contract_id)
        return response.data
    }

    async getContractRegister(id: number): Promise<GetContractRegisterResponse> {
        const response = await contractsRepository.getContractRegister(id)
        return response.data
    }

    async getContractByBin(bin: string): Promise<ContractsGetContractByBinResponseT> {
        const response = await contractsRepository.getContractByBin(bin)
        return response.data
    }

    async sendTranscript(payload: ContractsSendTranscriptParamsT): Promise<void> {
        const response = await contractsRepository.sendTranscript(payload)
        return response.data
    }

    async createContract(form_data: FormData): Promise<void> {
        await contractsRepository.createContract(form_data)
    }

    async addCommentRegister(id: number, comment: string): Promise<void> {
        await contractsRepository.addCommentRegister(id, comment)
    }

    async changeStatusRegister(id: number, statusId: number, comment?: string): Promise<void> {
        await contractsRepository.changeStatusRegister(id, statusId, comment)
    }

    async createContractRegister(payload: CreateContractRegisterPayload): Promise<void> {
        await contractsRepository.createContractRegister(payload)
    }

    async editContractRegister(id: number, payload: EditContractRegisterPayload): Promise<void> {
        await contractsRepository.editContractRegister(id, payload)
    }

    async sendD24(form_data: FormData): Promise<void> {
        await contractsRepository.sendD24(form_data)
    }

    async attachDocument(form_data: FormData): Promise<ContractsAttachDocumentResponseT> {
        const response = await contractsRepository.attachDocument(form_data)
        return response.data
    }

    async downloadContract(contract_id: number): Promise<ContractsDownloadContractResponseT> {
        const response = await contractsRepository.downloadContract(contract_id)
        return response.data
    }

    async showContract(contract_id: number): Promise<ContractsShowContractResponseT> {
        const response = await contractsRepository.showContract(contract_id)
        return response.data
    }

    async downloadDocument(document_id: number): Promise<ContractsDownloadDocumentResponseT> {
        const response = await contractsRepository.downloadDocument(document_id)
        return response.data
    }

    async showDocument(document_id: number): Promise<ContractsShowDocumentResponseT> {
        const response = await contractsRepository.showDocument(document_id)
        return response.data
    }

    async getTypes(params: ContractsGetTypesParamsT): Promise<ContractsGetTypesResponseT> {
        const response = await contractsRepository.getTypes(params)
        return response.data
    }

    async getStatuses(page: number): Promise<ContractsGetStatusesResponseT> {
        const response = await contractsRepository.getStatuses(page)
        return response.data
    }

    async getStatusesRegister(isVisible?: number): Promise<ContractsGetStatusesResponseT> {
        const response = await contractsRepository.getStatusesRegister(isVisible)
        return response.data
    }

    async exportExcel(params: ContractsExportExcelParamsT): Promise<any> {
        const response = await contractsRepository.exportExcel(params)
        return response.data
    }

    async exportExcelRegister(params: ContractsRegisterExportExcelParams): Promise<any> {
        const response = await contractsRepository.exportExcelRegister(params)
        return response.data
    }

    async downloadTranscript(transcript_id: number): Promise<any> {
        const response = await contractsRepository.downloadTranscript(transcript_id)
        return response.data
    }

    async updateContract(id: number, form_data: FormData): Promise<void> {
        const response = await contractsRepository.updateContract(id, form_data)
        return response.data
    }

    async addTemplate(form_data: FormData): Promise<void> {
        const response = await contractsRepository.addTemplate(form_data)
        return response.data
    }

    async deleteContract(contract_id: number): Promise<void> {
        await contractsRepository.deleteContract(contract_id)
    }

    async deleteContractById(contract_id: number): Promise<void> {
        await contractsRepository.deleteContractById(contract_id)
    }

    async deleteAdditionalContract(contract_id: number): Promise<void> {
        await contractsRepository.deleteAdditionalContract(contract_id)
    }

    async checkIsSigned(id: number): Promise<void> {
        await contractsRepository.checkIsSigned(id)
    }

    async deleteTemplate(id: number): Promise<void> {
        await contractsRepository.deleteTemplate(id)
    }

    async editTemplate(id: number, form_data: FormData): Promise<void> {
        await contractsRepository.editTemplate(id, form_data)
    }

    async downloadLogistBid(id: number): Promise<any> {
        const response = await contractsRepository.downloadLogistBid(id)
        return response.data
    }

    async getConfigList(params: GetConfigListParams): Promise<GetConfigListResponse> {
        const response = await contractsRepository.getConfigList(params)
        return response.data
    }

    async getConfigValueByKey(key: string): Promise<GetConfigValueByKeyResponse> {
        const response = await contractsRepository.getConfigValueByKey(key)
        return response.data
    }

    async updateConfigKey(key: string, value: string[]): Promise<void> {
        const response = await contractsRepository.updateConfigKey(key, value)
        return response.data
    }
}
